var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * React-query hooks for validation-rule administration (CZNA-245).
 *
 * Wraps `validationRulesService` so components stay free of `ky` plumbing and
 * cache invalidation is centralised. Keyed on the query params object so the
 * unstable `useOkapiKy()` reference inside the closure never retriggers the
 * query (see MEMORY / ui-czna-development skill).
 */
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { fetchValidationRules, fetchValidationRule, fetchValidationRuleHistory, fetchValidationRuleMeta, createValidationRule, updateValidationRule, deleteValidationRule, } from '../api/validationRulesService';
var RULES_KEY = 'validation-rules';
/** List of rules with filters + paging. `keepPreviousData` for smooth paging. */
export var useValidationRules = function (params) {
    var ky = useOkapiKy();
    return useQuery([RULES_KEY, params], function () { return fetchValidationRules(ky, params); }, { keepPreviousData: true });
};
/** Single rule detail. Disabled until an id is selected. */
export var useValidationRule = function (id) {
    var ky = useOkapiKy();
    return useQuery([RULES_KEY, 'detail', id], function () { return fetchValidationRule(ky, id); }, { enabled: !!id });
};
/** Audit trail for a rule. Disabled until an id is selected. */
export var useValidationRuleHistory = function (id, enabled) {
    if (enabled === void 0) { enabled = true; }
    var ky = useOkapiKy();
    return useQuery([RULES_KEY, 'history', id], function () { return fetchValidationRuleHistory(ky, id); }, { enabled: !!id && enabled });
};
/** Dropdown vocabularies. Cached longer — rarely changes within a session. */
export var useValidationRuleMeta = function () {
    var ky = useOkapiKy();
    return useQuery([RULES_KEY, 'meta'], function () { return fetchValidationRuleMeta(ky); }, { staleTime: 10 * 60 * 1000, cacheTime: 15 * 60 * 1000 });
};
/**
 * Create (POST) or update (PUT) a rule. The mutation discriminates on `mode`
 * so a single hook backs both the create-new and edit-existing flows.
 * Invalidates the list + detail families on success. Callers MUST supply
 * `onError` (project rule).
 */
export var useSaveValidationRule = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (args) {
        return args.mode === 'create'
            ? createValidationRule(ky, args.body)
            : updateValidationRule(ky, args.id, args.body);
    }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([RULES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
/**
 * Delete a MANUAL rule (or disable an ALEPH_CONVERTED one — see the caller in
 * ValidationRuleDetail which catches the 409 and PUTs `enabled=false`).
 * Invalidates the list family on success.
 */
export var useDeleteValidationRule = function (options) {
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    return useMutation(function (id) { return deleteValidationRule(ky, id); }, __assign(__assign({}, options), { onSuccess: function (data, vars, ctx) {
            var _a;
            queryClient.invalidateQueries([RULES_KEY]);
            (_a = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _a === void 0 ? void 0 : _a.call(options, data, vars, ctx);
        } }));
};
